import type { ReactNode } from 'react'

import Image from 'next/image'
import { Star } from 'lucide-react'

import { useGsap } from '@/hooks/use-gsap'
import { SplitWords } from '@/components/generals/split-words'
import { heroProof } from '@/constants/home'
import { siteConfig } from '@/constants/site'

export const Hero = (): ReactNode => {
    const scope = useGsap<HTMLElement>(({ gsap, select, reduced }) => {
        if (reduced) return

        const words = select('[data-hero-headline] .reveal-mask > span')

        // Entrance: the headline unfolds word by word, the proof row follows,
        // then the hero image settles in behind them.
        const intro = gsap.timeline({ defaults: { ease: 'nuvea' } })

        intro
            .from(words, {
                yPercent: 115,
                duration: 1.15,
                stagger: 0.055
            })
            .from(
                select('[data-hero-proof] > *'),
                { y: 18, opacity: 0, duration: 0.9, stagger: 0.07 },
                '-=0.75'
            )
            .from(
                select('[data-hero-statement] .reveal-mask > span'),
                { yPercent: 115, duration: 1, stagger: 0.04 },
                '-=0.9'
            )
            .from(
                select('[data-hero-visual-fade]'),
                { opacity: 0, duration: 1.3 },
                '-=1.05'
            )

        // The hero image grows and drifts as it travels through the viewport.
        gsap.fromTo(
            select('[data-hero-visual]'),
            { scale: 0.92 },
            {
                scale: 1.05,
                ease: 'none',
                scrollTrigger: {
                    trigger: select('[data-hero-stage]')[0],
                    start: 'top 80%',
                    end: 'bottom top',
                    scrub: 0.6
                }
            }
        )

        gsap.to(select('[data-hero-visual]'), {
            yPercent: -8,
            ease: 'none',
            scrollTrigger: {
                trigger: select('[data-hero-stage]')[0],
                start: 'top bottom',
                end: 'bottom top',
                scrub: 0.8
            }
        })

        // The wordmark rises slowly behind the image, the opposite way.
        gsap.fromTo(
            select('[data-hero-wordmark]'),
            { yPercent: 22 },
            {
                yPercent: -6,
                ease: 'none',
                scrollTrigger: {
                    trigger: select('[data-hero-stage]')[0],
                    start: 'top bottom',
                    end: 'bottom bottom',
                    scrub: 0.7
                }
            }
        )
    })

    return (
        <section ref={scope} id='hero' className='relative overflow-hidden bg-cream'>
            <div className='shell-x mx-auto w-full max-w-[1760px] pt-10 md:pt-16'>
                <div className='grid grid-cols-1 gap-10 lg:grid-cols-[1.4fr_1fr] lg:items-end lg:gap-16'>
                    <SplitWords
                        as='h1'
                        lines={['Skin that finally', 'settles.']}
                        className='type-display max-w-[16ch] text-ink'
                        data-hero-headline=''
                    />

                    <SplitWords
                        data-hero-statement=''
                        as='p'
                        lines={['We took things out', 'until only the five', 'that work were left.']}
                        className='type-lead max-w-[24ch] text-ink lg:justify-self-end lg:pb-3 lg:text-right'
                    />
                </div>

                <div data-hero-proof='' className='mt-8 flex flex-col gap-2 md:mt-10'>
                    <ul className='flex items-center'>
                        {heroProof.avatars.map((avatar, index) => (
                            <li
                                key={avatar.src}
                                className='relative size-7 overflow-hidden rounded-md not-first:-ml-1.5'
                                style={{ zIndex: heroProof.avatars.length - index }}
                            >
                                <Image
                                    src={avatar.src}
                                    alt={avatar.alt}
                                    fill
                                    sizes='28px'
                                    className='object-cover'
                                />
                            </li>
                        ))}
                    </ul>

                    <div
                        className='flex items-center gap-[3px]'
                        role='img'
                        aria-label='Rated five out of five stars'
                    >
                        {Array.from({ length: 5 }).map((_, index) => (
                            <Star
                                key={index}
                                className='size-3 fill-ink text-ink'
                                strokeWidth={0}
                                aria-hidden='true'
                            />
                        ))}
                    </div>

                    <p className='type-micro text-ink'>
                        <span className='font-semibold'>
                            {heroProof.count}
                        </span>{' '}
                        <span className='text-slate-soft'>
                            {heroProof.label}
                        </span>
                    </p>
                </div>
            </div>

            <div data-hero-stage='' className='relative mt-10 md:mt-16'>
                <div
                    data-hero-wordmark=''
                    aria-hidden='true'
                    className='pointer-events-none flex w-full justify-center leading-[0.78] font-medium tracking-[-0.045em] text-ink/[0.07] select-none'
                    style={{ fontSize: 'clamp(6rem, 25vw, 26rem)' }}
                >
                    {siteConfig.wordmark}
                </div>

                <div data-hero-visual-fade='' className='relative -mt-[18vw] md:-mt-[20vw]'>
                    <div
                        data-hero-visual=''
                        className='relative mx-auto aspect-[1738/905] w-full max-w-[1320px] will-change-transform'
                    >
                        <Image
                            src='/images/hero-product.png'
                            alt='An open palm with the Nuvéa bottle floating just above it'
                            fill
                            priority
                            sizes='(min-width: 1320px) 1320px, 100vw'
                            className='object-contain object-bottom'
                        />
                    </div>
                </div>
            </div>
        </section>
    )
}
